import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  Eye,
  EyeOff,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Lock,
  ArrowRight,
  Mail,
  Check,
  X,
} from "lucide-react";
import { z } from "zod";

const emailSchema = z.string().trim().email("Please enter a valid email address");

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(100, "Password must be less than 100 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");

type FlowState = "verifying" | "ready" | "error" | "request_mode" | "success";

interface PasswordCriteria {
  minLength: boolean;
  hasUpper: boolean;
  hasLower: boolean;
  hasNumber: boolean;
  matches: boolean;
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [flowState, setFlowState] = useState<FlowState>("verifying");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Password update form state
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Request new link state
  const [requestEmail, setRequestEmail] = useState("");
  const [requestingLink, setRequestingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Password criteria computation
  const criteria: PasswordCriteria = {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    matches: password.length > 0 && password === confirm,
  };

  const getStrengthScore = () => {
    let score = 0;
    if (criteria.minLength) score++;
    if (criteria.hasUpper) score++;
    if (criteria.hasLower) score++;
    if (criteria.hasNumber) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };

  const strengthScore = getStrengthScore();

  // Helper to parse hash params
  const parseHashParams = () => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.substring(1)
      : window.location.hash;
    return new URLSearchParams(hash);
  };

  useEffect(() => {
    let isMounted = true;

    const initAuthCheck = async () => {
      // 1. Check for errors in URL search or hash (e.g., otp_expired, access_denied)
      const hashParams = parseHashParams();
      const urlError = searchParams.get("error_description") ||
        searchParams.get("error") ||
        hashParams.get("error_description") ||
        hashParams.get("error");

      if (urlError) {
        if (!isMounted) return;
        setErrorMessage(
          urlError.includes("expired")
            ? "This password reset link has expired or has already been used. Please request a new link below."
            : urlError.replace(/\+/g, " ")
        );
        setFlowState("error");
        return;
      }

      // 2. Check for PKCE 'code' in query string
      const code = searchParams.get("code");
      if (code) {
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            if (!isMounted) return;
            setErrorMessage("The reset token is invalid or expired. Please request a new link.");
            setFlowState("error");
            return;
          }
          if (data.session && isMounted) {
            setFlowState("ready");
            return;
          }
        } catch {
          if (!isMounted) return;
          setErrorMessage("Failed to verify password reset code. Please request a new link.");
          setFlowState("error");
          return;
        }
      }

      // 3. Check for recovery tokens in hash
      const type = hashParams.get("type");
      const accessToken = hashParams.get("access_token");
      if (type === "recovery" || accessToken) {
        // Supabase client auto-processes hash token and fires onAuthStateChange
        if (isMounted) setFlowState("ready");
        return;
      }

      // 4. Check existing session / recovery session
      const { data: { session } } = await supabase.auth.getSession();
      if (session && isMounted) {
        setFlowState("ready");
        return;
      }

      // If no token or active session is found, allow user to request a link directly
      if (isMounted) {
        setFlowState("request_mode");
      }
    };

    initAuthCheck();

    // Listen to Supabase auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        setFlowState("ready");
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [searchParams]);

  // Handle countdown redirect on success
  useEffect(() => {
    if (flowState !== "success") return;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate("/auth");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [flowState, navigate]);

  // Handle Password Update Submit
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    const parseResult = passwordSchema.safeParse(password);
    if (!parseResult.success) {
      toast({
        variant: "destructive",
        title: "Weak password",
        description: parseResult.error.issues[0].message,
      });
      return;
    }

    if (password !== confirm) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please ensure both password fields match exactly.",
      });
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not update password",
        description: error.message,
      });
      return;
    }

    // Sign out to ensure clean credentials for subsequent logins
    await supabase.auth.signOut();

    toast({
      title: "Password updated successfully!",
      description: "You can now log in with your new password.",
    });
    setFlowState("success");
  };

  // Handle Requesting a New Link
  const handleRequestResetLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailSchema.safeParse(requestEmail);
    if (!result.success) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: result.error.issues[0].message,
      });
      return;
    }

    setRequestingLink(true);
    const { error } = await supabase.auth.resetPasswordForEmail(result.data, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setRequestingLink(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not send reset link",
        description: error.message,
      });
      return;
    }

    setLinkSent(true);
    toast({
      title: "Reset link sent",
      description: `Check ${result.data} for instructions to reset your password.`,
    });
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 sm:px-6 py-12 relative overflow-hidden">
      {/* Background glow styling */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md space-y-6 relative z-10">
        {/* Header Branding */}
        <div className="text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-zinc-800 shadow-xl flex items-center justify-center mx-auto text-white">
            {flowState === "success" ? (
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            ) : flowState === "error" ? (
              <AlertCircle className="w-7 h-7 text-red-400" />
            ) : (
              <ShieldCheck className="w-7 h-7 text-purple-400" />
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {flowState === "success"
              ? "Password Updated!"
              : flowState === "ready"
              ? "Set a new password"
              : flowState === "error"
              ? "Reset Link Expired"
              : flowState === "request_mode"
              ? "Reset Your Password"
              : "Verifying Link..."}
          </h1>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            {flowState === "success"
              ? "Your password has been changed. You can now sign in with your new credentials."
              : flowState === "ready"
              ? "Choose a strong new password for your Metro City Groove account."
              : flowState === "error"
              ? errorMessage
              : flowState === "request_mode"
              ? "Enter your account email and we'll send you a secure link to create a new password."
              : "Checking authorization tokens from your email link..."}
          </p>
        </div>

        {/* 1. LOADING / VERIFYING STATE */}
        {flowState === "verifying" && (
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 text-center space-y-4">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto" />
            <p className="text-sm text-gray-400">Verifying your secure password reset session...</p>
          </div>
        )}

        {/* 2. SUCCESS STATE */}
        {flowState === "success" && (
          <div className="bg-zinc-950 border border-emerald-900/50 rounded-2xl p-6 sm:p-8 space-y-6 text-center">
            <div className="space-y-2">
              <p className="text-sm text-gray-300">
                You will be automatically redirected to the sign-in page in{" "}
                <span className="font-semibold text-emerald-400">{countdown}s</span>.
              </p>
            </div>

            <Button
              type="button"
              onClick={() => navigate("/auth")}
              className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-5 text-sm sm:text-base flex items-center justify-center gap-2 rounded-xl transition-all"
            >
              Sign In Now <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* 3. READY STATE: NEW PASSWORD FORM */}
        {flowState === "ready" && (
          <form
            onSubmit={handlePasswordUpdate}
            className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 sm:p-8 space-y-5 shadow-2xl"
          >
            {/* New Password Input */}
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-gray-300 text-sm font-medium">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  autoComplete="new-password"
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-purple-500 focus:ring-purple-500/20 pr-10 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Password Strength Indicator */}
            {password.length > 0 && (
              <div className="space-y-2 bg-zinc-900/60 border border-zinc-800/80 rounded-xl p-3 text-xs">
                <div className="flex justify-between items-center text-gray-400">
                  <span>Strength:</span>
                  <span
                    className={`font-semibold ${
                      strengthScore <= 2
                        ? "text-red-400"
                        : strengthScore <= 4
                        ? "text-amber-400"
                        : "text-emerald-400"
                    }`}
                  >
                    {strengthScore <= 2 ? "Weak" : strengthScore <= 4 ? "Good" : "Strong"}
                  </span>
                </div>
                <div className="flex gap-1.5 h-1.5 w-full">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`flex-1 rounded-full transition-all duration-300 ${
                        i < strengthScore
                          ? strengthScore <= 2
                            ? "bg-red-500"
                            : strengthScore <= 4
                            ? "bg-amber-500"
                            : "bg-emerald-500"
                          : "bg-zinc-800"
                      }`}
                    />
                  ))}
                </div>

                {/* Criteria checklist */}
                <div className="grid grid-cols-2 gap-1.5 pt-2 text-[11px] text-gray-400">
                  <span className={`flex items-center gap-1 ${criteria.minLength ? "text-emerald-400" : ""}`}>
                    {criteria.minLength ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-zinc-600" />}
                    8+ Characters
                  </span>
                  <span className={`flex items-center gap-1 ${criteria.hasUpper ? "text-emerald-400" : ""}`}>
                    {criteria.hasUpper ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-zinc-600" />}
                    Uppercase letter
                  </span>
                  <span className={`flex items-center gap-1 ${criteria.hasLower ? "text-emerald-400" : ""}`}>
                    {criteria.hasLower ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-zinc-600" />}
                    Lowercase letter
                  </span>
                  <span className={`flex items-center gap-1 ${criteria.hasNumber ? "text-emerald-400" : ""}`}>
                    {criteria.hasNumber ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-zinc-600" />}
                    Number
                  </span>
                </div>
              </div>
            )}

            {/* Confirm Password Input */}
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-gray-300 text-sm font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat new password"
                  required
                  autoComplete="new-password"
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-purple-500 focus:ring-purple-500/20 pr-10 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirm.length > 0 && password !== confirm && (
                <p className="text-xs text-red-400 flex items-center gap-1 mt-1">
                  <X className="w-3 h-3" /> Passwords do not match
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={submitting || !criteria.minLength || !criteria.hasUpper || !criteria.hasLower || !criteria.hasNumber || !criteria.matches}
              className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-5 text-sm sm:text-base rounded-xl transition-all shadow-lg hover:shadow-white/10"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating Password...
                </>
              ) : (
                "Update Password"
              )}
            </Button>
          </form>
        )}

        {/* 4. ERROR / EXPIRED / REQUEST STATE */}
        {(flowState === "error" || flowState === "request_mode") && (
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 sm:p-8 space-y-6 shadow-2xl">
            {flowState === "error" && (
              <Alert className="bg-red-950/40 border-red-900/50 text-red-300">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertTitle className="text-red-200 font-semibold">Link Expired or Invalid</AlertTitle>
                <AlertDescription className="text-xs text-red-300/90 mt-1">
                  {errorMessage || "Password reset links are single-use and time-sensitive. You can request a fresh link below."}
                </AlertDescription>
              </Alert>
            )}

            {linkSent ? (
              <div className="text-center space-y-4 py-3">
                <div className="w-12 h-12 rounded-full bg-emerald-950/60 border border-emerald-800/60 flex items-center justify-center mx-auto text-emerald-400">
                  <Mail className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-white">Reset Link Sent</h3>
                  <p className="text-xs text-gray-400">
                    We've emailed a password reset link to <span className="text-white font-medium">{requestEmail}</span>. Click the link in your email to choose a new password.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLinkSent(false)}
                  className="border-zinc-800 hover:bg-zinc-900 text-gray-300"
                >
                  Send to a different email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleRequestResetLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="request-email" className="text-gray-300 text-sm font-medium">
                    Account Email Address
                  </Label>
                  <div className="relative">
                    <Input
                      id="request-email"
                      type="email"
                      value={requestEmail}
                      onChange={(e) => setRequestEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-purple-500 focus:ring-purple-500/20 rounded-xl"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={requestingLink}
                  className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-5 text-sm rounded-xl transition-all"
                >
                  {requestingLink ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending Link...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
            )}
          </div>
        )}

        {/* Footer Navigation */}
        <div className="text-center pt-2">
          <button
            type="button"
            onClick={() => navigate("/auth")}
            className="text-xs sm:text-sm text-gray-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
          >
            <Lock className="w-3.5 h-3.5" /> Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
